import type { KeyboardEvent } from "react";
import { useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import {
    AccountSelect
} from "app/pages/sales-intelligence/modules/crm-export/components/AccountSelect/AccountSelect";
import { NEW_ACCOUNT_VALUE } from "app/pages/sales-intelligence/domain/crm-integration/constants/account";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import type { AccountExportPreview } from "../../types";

import { ACCOUNTS_EXPORT_PREVIEW_MODAL_CLASS } from "../../constants";
import {
    StyledAccountControl,
    StyledAccountListItem,
    StyledCheckIconButton,
    StyledCompanyName,
    StyledEditIconButton,
    StyledNewAccountInput,
    StyledSelectWrap,
} from "../styles";

type AccountExportPreviewItemProps = {
    item: AccountExportPreview;
    integrationName: CrmIntegrationProvider;
    canCreateNewAccount: boolean;
    index: number;
    onChange(item: AccountExportPreview): void;
};

export const AccountExportPreviewItem = (props: AccountExportPreviewItemProps) => {
    const { item, integrationName, canCreateNewAccount, index, onChange } = props;
    const { domain, companyName, matchedCompanies, accountId, searchedCompanyLabel, newAccountName } = item;

    const translate = useTranslation();

    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState("");

    const isNewAccount = accountId === NEW_ACCOUNT_VALUE;
    const defaultName = companyName || domain;
    const displayName = newAccountName || defaultName;

    const handleAccountChange = (nextAccountId: string, searchedLabel?: string) => {
        onChange({ ...item, accountId: nextAccountId, searchedCompanyLabel: searchedLabel ?? searchedCompanyLabel });
    };

    const startEditing = () => {
        setDraft(displayName);
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
    };

    const confirmEditing = () => {
        const trimmed = draft.trim();
        // Empty input resets to the default (original) name.
        onChange({ ...item, newAccountName: trimmed || undefined });
        setIsEditing(false);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            confirmEditing();
        }

        if (event.key === "Escape") {
            cancelEditing();
        }
    };

    return (
        <StyledAccountListItem data-automation={`accounts-export-preview-item-${index}`}>
            <Stack overflow="hidden">
                <StyledCompanyName fontVariant="subtitle2">{companyName || domain}</StyledCompanyName>
                <Text fontVariant="input2" color={colorsPalettes.carbon[300]}>{domain}</Text>
            </Stack>
            <StyledAccountControl alignItems="center" gap="16px" isNewAccount={isNewAccount}>
                <StyledSelectWrap>
                    {isEditing ? (
                        <StyledNewAccountInput
                            autoFocus
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            onKeyDown={handleKeyDown}
                            onBlur={cancelEditing}
                            placeholder={translate(
                                "si.advanced_search.accounts_export_preview_modal.new_account_input_placeholder",
                            )}
                            data-automation={`accounts-export-preview-item-${index}-name-input`}
                        />
                    ) : (
                        <AccountSelect
                            suggestedAccounts={matchedCompanies}
                            contactsCompanyName={displayName}
                            integrationName={integrationName}
                            onChange={handleAccountChange}
                            value={accountId}
                            canCreateNewAccount={canCreateNewAccount}
                            containerClassName={ACCOUNTS_EXPORT_PREVIEW_MODAL_CLASS}
                            searchedCompanyLabel={searchedCompanyLabel}
                        />
                    )}
                </StyledSelectWrap>
                {isNewAccount ? (
                    isEditing ? (
                        // Prevent the input's blur (which cancels editing) from firing before the click.
                        <Flex inline flexShrink={0} onMouseDown={(event) => event.preventDefault()}>
                            <StyledCheckIconButton
                                iconName="checked"
                                iconSize="xs"
                                type="flat"
                                onClick={confirmEditing}
                                dataAutomation={`accounts-export-preview-item-${index}-name-confirm`}
                            />
                        </Flex>
                    ) : (
                        <StyledEditIconButton
                            iconName="edit-icon"
                            iconSize="xs"
                            type="flat"
                            onClick={startEditing}
                            dataAutomation={`accounts-export-preview-item-${index}-name-edit`}
                        />
                    )
                ) : null}
            </StyledAccountControl>
        </StyledAccountListItem>
    );
};
